<script setup lang="ts">
import { onMounted, watch } from 'vue'
import { useI18n } from 'vue-i18n'
import PublicSiteFooter from '@/components/publicSite/PublicSiteFooter.vue'
import PublicSiteHeader from '@/components/publicSite/PublicSiteHeader.vue'
import { usePublicPricingPlans } from '@/composables/usePublicPricingPlans'

definePage({
  meta: {
    layout: 'blank',
    public: true,
  },
})

const { t, locale } = useI18n({ useScope: 'global' })
const { plans, fetchPlans } = usePublicPricingPlans()

const faqKeys = ['trial', 'upgrade', 'cancel', 'payment'] as const

const setupSEO = () => {
  if (typeof document === 'undefined') return

  const title = t('publicSite.pricing.seoTitle')
  const description = t('publicSite.pricing.seoDescription')
  const url = window.location.origin + '/prezzi'

  document.title = title

  const setMeta = (name: string, content: string, isProperty = false) => {
    let el = document.querySelector(`meta[${isProperty ? 'property' : 'name'}="${name}"]`)
    if (!el) {
      el = document.createElement('meta')
      if (isProperty) el.setAttribute('property', name)
      else el.setAttribute('name', name)
      document.head.appendChild(el)
    }
    el.setAttribute('content', content)
  }

  setMeta('description', description)
  setMeta('og:title', title, true)
  setMeta('og:description', description, true)
  setMeta('og:url', url, true)
  setMeta('twitter:title', title)
  setMeta('twitter:description', description)
}

onMounted(() => {
  fetchPlans()
  setupSEO()
})

watch(locale, () => setupSEO())
</script>

<template>
  <div class="pricing-premium">
    <PublicSiteHeader />

    <!-- Hero -->
    <header class="pricing-hero">
      <div class="container text-center">
        <h1 class="text-h1 hero-title mb-6">{{ t('publicSite.pricing.heroTitle') }}</h1>
        <p class="text-h6 hero-description mx-auto">{{ t('publicSite.pricing.heroDescription') }}</p>
      </div>
    </header>

    <!-- Pricing Cards -->
    <section class="pricing-section pb-16">
      <div class="container">
        <VRow justify="center" align="stretch">
          <VCol v-for="p in plans" :key="p.name" cols="12" md="4" class="d-flex">
            <div class="pricing-card" :class="{ 'popular-card': p.popular }">
              <div v-if="p.popular" class="popular-badge">{{ t('publicSite.landing.popularBadge') }}</div>
              <div class="pa-8">
                <h2 class="text-h4 font-weight-bold plan-title mb-2">{{ p.name }}</h2>
                <p class="text-body-2 plan-description mb-6">{{ p.desc }}</p>

                <div class="d-flex align-baseline mb-8">
                  <span class="text-h3 font-weight-black text-white">€{{ p.price }}</span>
                  <span class="text-body-1 plan-period ms-2">{{ t('publicSite.landing.perMonth') }}</span>
                </div>

                <VDivider class="border-white-opacity-10 mb-8" />

                <ul class="feature-list mb-10">
                  <li v-for="feat in p.features" :key="feat" class="d-flex align-center mb-4">
                    <VIcon icon="tabler-circle-check" color="primary" size="20" class="me-3" />
                    <span class="text-body-2 plan-feature-text">{{ feat }}</span>
                  </li>
                </ul>

                <VBtn
                  block
                  size="x-large"
                  :variant="p.popular ? 'elevated' : 'outlined'"
                  :color="p.popular ? 'primary' : 'white'"
                  class="font-weight-bold text-none rounded-xl"
                  :class="{ 'btn-glow': p.popular }"
                  to="/register"
                >
                  {{ p.button }}
                </VBtn>
              </div>
            </div>
          </VCol>
        </VRow>
      </div>
    </section>

    <!-- FAQ -->
    <section class="faq-section pb-16">
      <div class="container" style="max-width: 800px;">
        <h2 class="text-h3 font-weight-black text-center mb-10 section-title">{{ t('publicSite.pricing.faqTitle') }}</h2>
        <VExpansionPanels variant="accordion" class="faq-panels">
          <VExpansionPanel v-for="key in faqKeys" :key="key">
            <VExpansionPanelTitle>{{ t(`publicSite.pricing.faq.${key}.q`) }}</VExpansionPanelTitle>
            <VExpansionPanelText>{{ t(`publicSite.pricing.faq.${key}.a`) }}</VExpansionPanelText>
          </VExpansionPanel>
        </VExpansionPanels>
      </div>
    </section>

    <!-- CTA -->
    <section class="cta-banner">
      <div class="container text-center py-16">
        <div class="cta-inner glass-effect pa-16 rounded-xl overflow-hidden position-relative">
          <h2 class="text-h2 font-weight-black cta-title mb-8 position-relative">
            {{ t('publicSite.landing.ctaTitle') }}
          </h2>
          <VBtn size="x-large" color="white" class="text-primary px-16 py-6 text-h6 font-weight-bold position-relative" to="/register">
            {{ t('publicSite.shared.createYourAppNow') }}
          </VBtn>
        </div>
      </div>
    </section>

    <PublicSiteFooter />
  </div>
</template>

<style scoped>
@import url('https://fonts.googleapis.com/css2?family=Outfit:wght@300;400;600;800&display=swap');

.pricing-premium {
  font-family: 'Outfit', sans-serif;
  background-color: #0F172A;
  color: #F1F5F9;
  min-height: 100vh;
  overflow-x: hidden;
}

.container {
  max-width: 1600px;
  margin: 0 auto;
  padding: 0 40px;
}

.pricing-hero {
  padding: 100px 0 60px;
}

.hero-title {
  color: #FFFFFF;
}

.hero-description {
  color: #E5EEF9;
  max-width: 640px;
}

.section-title {
  color: #FFFFFF;
}

.pricing-card {
  background: rgba(255, 255, 255, 0.03);
  border: 1px solid rgba(255, 255, 255, 0.05);
  border-radius: 32px;
  width: 100%;
  position: relative;
  transition: all 0.4s cubic-bezier(0.4, 0, 0.2, 1);
  overflow: hidden;
}

.pricing-card:hover {
  transform: translateY(-10px);
  background: rgba(255, 255, 255, 0.05);
  border-color: rgba(255, 255, 255, 0.1);
}

.popular-card {
  background: rgba(99, 102, 241, 0.08);
  border: 2px solid #6366F1;
  transform: scale(1.05);
  z-index: 2;
}

.popular-badge {
  background: #6366F1;
  color: white;
  font-size: 12px;
  font-weight: 800;
  padding: 6px 16px;
  position: absolute;
  top: 0;
  left: 50%;
  transform: translateX(-50%);
  border-radius: 0 0 12px 12px;
  letter-spacing: 1px;
}

.feature-list {
  list-style: none;
  padding: 0;
}

.plan-title {
  color: #FFFFFF;
}

.plan-description,
.plan-feature-text {
  color: #E2E8F0;
}

.plan-period {
  color: #CBD5E1;
}

.faq-panels :deep(.v-expansion-panel) {
  background: rgba(255, 255, 255, 0.03) !important;
  color: #F1F5F9;
  margin-bottom: 8px;
  border-radius: 12px !important;
}

.faq-panels :deep(.v-expansion-panel-text__wrapper) {
  color: #CBD5E1;
}

.cta-inner {
  background: #1E293B;
  border: 1px solid rgba(255, 255, 255, 0.1);
}

.cta-title {
  color: #FFFFFF;
}

.btn-glow {
  box-shadow: 0 0 20px rgba(99, 102, 241, 0.3);
}

@media (max-width: 960px) {
  .popular-card,
  .popular-card:hover {
    transform: none;
  }
}

@media (max-width: 600px) {
  .container { padding: 0 20px; }
}
</style>
